import React, { useState } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import { PaginationComponent, MasonryComponent } from '@zolteam/axenergie-ui-library';
// Constants
import { useRouter } from 'next/router';
import titleMetaDescription from '../../constants/TitleMetaDescription';
import strings from '../../constants/Strings';
// Components
import Menu from '../../components/Molecules/Menu';
import HeaderWithSlashContainer from '../../components/Atoms/HeaderWithSlashContainer';
import NewsCard from '../../components/Atoms/NewsCard';
import Footer from '../../components/Molecules/Footer';
import SearchEngine from '../../components/Organisms/SearchEngine';
import ReassuranceBanner from '../../components/Molecules/ReassuranceBanner';
// Services and hooks
import menuAndFooterService from '../../services/contentful/menuAndFooterService';
import useIsMobile from '../../hooks/useIsMobile';
import searchEngineUtils from '../../utils/searchEngineUtils';

// Utils
import utils from '../../utils/formatCMS';
// Images
import logo from '../../public/Images/logo.webp';
import AgencyService from '../../services/api/AgencyService';
import urlUtils from '../../utils/urlUtils';

const AdviceList = (props) => {
  const [isMobile,, screenWidth] = useIsMobile();
  const [currentList, setCurrentList] = useState([]);
  const itemsPerPage = 12;
  const [currentPage, setCurrentPage] = useState(1);

  const router = useRouter();

  const getCardWidth = () => (screenWidth < 432 ? screenWidth - 32 : 400);

  return (
    <div>
      <Head>
        <title>{titleMetaDescription.adviceListPage.title}</title>
        <meta
          property="og:title"
          content={titleMetaDescription.adviceListPage.title}
        />
        <meta
          property="og:image"
          content="/Images/advice-illustration.webp"
        />
        <meta
          property="og:image:secure_url"
          content="/Images/advice-illustration.webp"
        />
        <meta name="description" content={titleMetaDescription.adviceListPage.description} />
      </Head>
      {/* header */}
      <div className="full-width d-flex f-row relative">
        {/* Menu */}
        <div className="absolute absolute-right-top-corner index-2">
          <Menu items={utils.formatCMSDatas('menu', props)} fontColor="grey-800-text" />
        </div>
        <HeaderWithSlashContainer imageUrl="/Images/advice-illustration.webp">
          {isMobile ? null : (
            <button type="button" onClick={() => router.push('/')}>
              <Image alt="logo" src={logo} width={206} height={206} />
            </button>
          )}
          <div className={`${isMobile ? 'mv-2' : 'mv-6'} pl-5`}>
            <h1 className="grey-800-text medium-weight">{strings.advicesAndTips}</h1>
            <p className="grey-600-text normal-weight">{strings.advicesAndTipsSubtitle}</p>
          </div>
        </HeaderWithSlashContainer>
      </div>
      <div className={`grey-100-background ${isMobile ? 'p-3 d-flex justify-center' : 'p-5'}`}>
        <div style={{ width: isMobile ? getCardWidth() : '100%', marginLeft: isMobile ? '-12px' : 0 }}>
          {currentList?.length
            ? (
              <MasonryComponent columnWidth={getCardWidth() + 20} containerWidth={screenWidth}>
                {
                  currentList?.map(({
                    title, id, content, illustrationFilePath,
                  }) => (
                    <NewsCard
                      key={id}
                      width={getCardWidth()}
                      content={content}
                      className="grid-item"
                      title={title}
                      image={illustrationFilePath}
                      link={urlUtils.getConseilUrl({ id, title })}
                    />
                  ))
                }
              </MasonryComponent>
            )
            : null }
          <PaginationComponent
            setCurrentList={setCurrentList}
            totalList={props.contents || []}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>
      {/* Search Engine */}
      <div className={`grey-800-background 
      ${isMobile ? 'ph-2 pv-4' : 'p-5'} full-width d-flex justify-center align-center f-column`}
      >
        <p className="h2 white-text medium-weight m-0 text-align-center">{strings.findYourHeatingPro}</p>
        <p className={`grey-400-text normal-weight text-align-center ${isMobile ? 'ph-2' : 'ph-6'} mv-4`}>
          {strings.findYourHeatingProSubtitle}
        </p>
        <div className="d-flex justify-center align-center white-background radius-5">
          <SearchEngine
            theme="light"
            withLogo={false}
            withLabel
            handleSubmit={(data) => searchEngineUtils.goToSearchPage(router, data)}
          />
        </div>
      </div>
      {/* Reassurance banner */}
      <ReassuranceBanner items={utils.formatCMSReassurance(props)} />
      {/* Footer */}
      <Footer
        networkBtn={props.footerRseauLienBouton}
        clientItems={utils.formatCMSDatas('footerClient', props)}
        networkItems={utils.formatCMSDatas('footerRseau', props)}
        facebook={props.footerFacebook}
        instagram={props.footerInstagram}
        twitter={props.footerTwitter}
        mentions={props.mentions}
        legalItems={utils.formatCMSDatas('sousFooter', props)}
        isMobile={isMobile}
      />
    </div>
  );
};

export default AdviceList;

export async function getServerSideProps() {
  let menuAndFooter;
  let contents;
  try {
    menuAndFooter = await menuAndFooterService.menuAndFooter();
  } catch (err) {
    menuAndFooter = {};
    console.error(err);
  }
  try {
    contents = await AgencyService.getAgencyPageContents({});
  } catch (err) {
    contents = [{}];
    console.error(err);
  }
  return {
    props: {
      ...menuAndFooter?.fields,
      contents: contents?.data?.agencyPageContents || [],
    },
  };
}
